import { Editor, Text } from 'slate';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-light-svg-icons/faChevronUp';
import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ReactEditor } from 'slate-react';
import { useTranslation } from 'react-i18next';
import { usePlateEditorRef } from '@udecode/plate';

import AITextCompletion from 'models/ai_text_completion';
import AITextCompletionContext from 'components/text_editor_new/plugins/ai/components/ai_text_completion_context';
import {
  Box,
  Content,
  Options,
  LoadingContent,
  PrimaryButton,
  SecondaryButton,
  TextButton,
  TrailerIcon,
} from './ai_text_completion_preview_styles';
import CancelAITextCompletion from 'actions/ai_text_completions/cancel_ai_text_completion';
import ComposerContext from 'components/composer/contexts/composer_context';
import CustomerContext from 'components/customer/customer_context';
import EditorScrollContext from 'components/composer/shared/editor_scroll_context';
import LoadingDots from 'components/lib/loading_dots';
import { MARK_AI_TEXT_COMPLETION_SELECTION } from 'components/text_editor_new/plugins/ai/ai_text_completion';
import RetryAITextCompletion from 'actions/ai_text_completions/retry_ai_text_completion';
import TranslateAgainAITextCompletion from 'actions/ai_text_completions/translate_again_ai_text_completion';
import AiTranslateIntoContextMenu from 'components/text_editor_new/plugins/ai/components/ai_translate_into_context_menu';
import TranslationLanguages from 'models/languages/translation_languages';
import SafeHtml from 'components/common/safe_html';
import UseAITextCompletion from 'actions/ai_text_completions/use_ai_text_completion';
import useExecuteAction from 'components/hooks/use_execute_action';
import { usePopoverMenu } from 'components/common/menu';

export default function AITextCompletionPreviewWrapper() {
  const { completion, isLoading, requestorId } = useContext(AITextCompletionContext);
  if ((!isLoading && !completion) || !requestorId) {
    return null;
  }

  if (isLoading) {
    return <AITextCompletionLoading />;
  }

  return <AITextCompletionPreview completion={completion} />;
}

export function AITextCompletionPreview({ completion }) {
  const { t } = useTranslation();
  const editor = usePlateEditorRef();
  const { cancel, replace, translateAgain, tryAgain } = useCompletionActions();
  const boxRef = useRef(null);

  usePositionPreview(editor, boxRef);
  useScrollToPreview(boxRef, completion);

  const text = completion?.data?.text || '';

  return (
    <Box data-aid="textCompletionPreview" ref={boxRef}>
      <Content data-aid="textCompletionPreview-content">
        <SafeHtml html={text} setDirection />
      </Content>
      <Options>
        <PrimaryButton onClick={replace}>{t("Use 'tab'")}</PrimaryButton>
        <SecondaryButton onClick={tryAgain}>{t("Retry 'r'")}</SecondaryButton>
        <SecondaryButton onClick={cancel}>{t("Cancel 'esc'")}</SecondaryButton>
        {completion?.type === AITextCompletion.Types.translate && <LanguageSelect onSelect={translateAgain} />}
      </Options>
    </Box>
  );
}

function LanguageSelect({ onSelect }) {
  const [isTranslateIntoMenuVisible, setTranslateIntoMenuVisible] = useState(false);
  const { targetLanguage } = useContext(AITextCompletionContext);
  const { targetRef, setTargetRef } = usePopoverMenu();

  const defaultLanguage = TranslationLanguages[targetLanguage] || targetLanguage;

  return (
    <>
      <TextButton data-aid="textCompletionPreview-languageSelect" onClick={openTranslateIntoMenu} ref={setTargetRef}>
        {defaultLanguage}
        <TrailerIcon icon={isTranslateIntoMenuVisible ? faChevronUp : faChevronDown} />
      </TextButton>
      <AiTranslateIntoContextMenu
        handleSelectLanguage={onSelect}
        isOpen={isTranslateIntoMenuVisible}
        lastSelectedLanguage={targetLanguage}
        onClose={closeTranslateIntoMenu}
        position="top"
        targetRef={targetRef}
      />
    </>
  );

  function closeTranslateIntoMenu() {
    setTranslateIntoMenuVisible(false);
  }

  function openTranslateIntoMenu() {
    setTranslateIntoMenuVisible(true);
  }
}

function AITextCompletionLoading() {
  const { t } = useTranslation();
  const editor = usePlateEditorRef();
  const boxRef = useRef(null);
  usePositionPreview(editor, boxRef);
  useScrollToPreview(boxRef, null);

  return (
    <Box ref={boxRef}>
      <LoadingContent>
        {t('Got it! Writing')} <LoadingDots />
      </LoadingContent>
    </Box>
  );
}

function usePositionPreview(editor, boxRef) {
  useLayoutEffect(() => {
    if (!boxRef.current) {
      return;
    }

    const lastNode = getLastSelectionNode(editor);
    if (!lastNode) {
      return;
    }

    const domNode = ReactEditor.toDOMNode(editor, lastNode);
    if (domNode) {
      // Position the text assist box right below the last AI Text rewrite selection node
      const targetRect = domNode.getBoundingClientRect();
      const top = `${domNode.offsetTop + targetRect.height + 8}px`;
      boxRef.current.style.top = top;
      boxRef.current.style.position = 'absolute';
    }
  });
}

function getLastSelectionNode(editor) {
  for (const [node] of Editor.nodes(editor, {
    match: n => Text.isText(n) && n[MARK_AI_TEXT_COMPLETION_SELECTION],
    reverse: true,
  })) {
    return node;
  }
  return null;
}

// Each time the completion changes, we want to smoothly scroll the box into view so that the agent
// can see the completion.
function useScrollToPreview(boxRef, completion) {
  const parentScrollNode = useContext(EditorScrollContext);

  useLayoutEffect(() => {
    if (!boxRef.current || !parentScrollNode) {
      return;
    }

    const timeoutId = setTimeout(() => boxRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' }), 10);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [boxRef, completion, parentScrollNode]);
}

function useCompletionActions() {
  const { completion, requestorId, setTextToInsert } = useContext(AITextCompletionContext);
  const { customerId } = useContext(CustomerContext);
  const { composition } = useContext(ComposerContext);
  const executeAction = useExecuteAction();

  const compositionRef = useRef(composition);
  useEffect(() => {
    compositionRef.current = composition;
  }, [composition]);

  // Cancel the text completion if the component or customer being rendered changes
  useEffect(() => {
    return () => {
      executeAction(CancelAITextCompletion, { customerId, requestorId, composition, source: 'rerender' });
    };
    // Note we only want to cancel the AI text completion if customerId or compositionId changes.. we don't
    // want to listen strictly to composition changes.. because the composition model updates pretty frequently.
  }, [executeAction, customerId, requestorId, composition.id]);

  const cancel = useCallback(() => {
    executeAction(CancelAITextCompletion, {
      customerId,
      requestorId,
      composition: compositionRef.current,
      source: 'user',
    });
  }, [executeAction, customerId, requestorId, compositionRef]);

  const replace = useCallback(() => {
    const text = completion?.data?.text;
    if (text) {
      setTextToInsert(text);
      executeAction(UseAITextCompletion, { customerId, requestorId, composition: compositionRef.current, text });
    } else {
      executeAction(CancelAITextCompletion, {
        customerId,
        requestorId,
        composition: compositionRef.current,
        source: 'blank_text',
      });
    }
  }, [executeAction, completion, setTextToInsert, customerId, requestorId, compositionRef]);

  const translateAgain = useCallback(
    targetLanguage => {
      executeAction(TranslateAgainAITextCompletion, {
        composition: compositionRef.current,
        customerId,
        requestorId,
        targetLanguage,
      });
    },
    [executeAction, completion, customerId, requestorId, compositionRef]
  );

  const tryAgain = useCallback(() => {
    executeAction(RetryAITextCompletion, { customerId, requestorId, composition: compositionRef.current });
  }, [executeAction, customerId, requestorId, compositionRef]);

  // Handle keyboard shortcuts for using text completion
  useEffect(() => {
    function onKeyDown(e) {
      if (e.ctrlKey || e.altKey || e.metaKey) {
        return;
      }

      if (e.key === 'Tab') {
        e.preventDefault();
        replace();
      } else if (e.key && e.key.toLowerCase() === 'r') {
        e.preventDefault();
        tryAgain();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancel();
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [replace, tryAgain, cancel]);

  return { cancel, completion, replace, translateAgain, tryAgain };
}
